/**
 * AdminBetaInvites — Mar 2026 · Soulumo beta allow-list panel.
 *
 * Route: `/admin/beta-invites` (admin-only).
 *
 * The simplest possible UI: a one-field "add email" box at the top,
 * a table of invited testers below, with a tiny "✓ joined" marker
 * for anyone who has actually registered. Removing an invite that
 * has already been used does NOT revoke the account — it just
 * cleans the list so you can see who's still outstanding.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import { ArrowLeft, Mail, Trash2, Check, Loader2, Plus, KeyRound, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const BRANDS = [
  { key: "soulumo", label: "Soulumo" },
  { key: "reclaim", label: "Reclaim" },
];

export default function AdminBetaInvites() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("soulumo");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/beta-invites`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setAdding(true);
    try {
      const r = await fetch(`${API}/api/admin/beta-invites`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, brand, note }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      setEmail("");
      setNote("");
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this invite? (Does not delete the account if they've already registered.)")) return;
    try {
      const r = await fetch(`${API}/api/admin/beta-invites/${id}`, {
        method: "DELETE",
        headers: auth(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const grouped = BRANDS.map((b) => ({
    ...b,
    items: items.filter((it) => it.brand === b.key),
  }));

  return (
    <AdminLayout
      title="Beta Invites"
      subtitle="Allow-list for narrow-brand sign-ups. Add a tester's email here before you share the link."
    >
      <button
        type="button"
        onClick={() => nav("/admin")}
        className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        data-testid="admin-beta-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </button>

      {/* Add form */}
      <form
        onSubmit={add}
        className="mb-10 bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3"
        data-testid="admin-beta-add-form"
      >
        <div className="flex items-center gap-2 text-slate-900 font-medium">
          <Mail className="w-4 h-4" /> Invite a tester
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_1fr_auto] gap-2">
          <input
            type="email"
            required
            placeholder="their@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid="admin-beta-email"
          />
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid="admin-beta-brand"
          >
            {BRANDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="note (optional) — Anna, book club"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid="admin-beta-note"
          />
          <button
            type="submit"
            disabled={adding || !email.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50"
            data-testid="admin-beta-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {adding ? "Adding…" : "Invite"}
          </button>
        </div>
        {err && (
          <div className="text-sm text-red-600" data-testid="admin-beta-error">{err}</div>
        )}
      </form>

      {/* Shared beta codes */}
      <BetaCodesSection />

      {/* Lists */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map((group) => (
            <section key={group.key} data-testid={`admin-beta-group-${group.key}`}>
              <div className="flex items-end justify-between mb-3">
                <h2 className="text-slate-900 font-semibold">{group.label}</h2>
                <div className="text-xs text-slate-500">
                  {group.items.length} invite{group.items.length === 1 ? "" : "s"}
                </div>
              </div>
              {group.items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500 text-center">
                  Nobody invited to {group.label} yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-4 font-normal">Email</th>
                        <th className="py-2 pr-4 font-normal">Note</th>
                        <th className="py-2 pr-4 font-normal">Invited</th>
                        <th className="py-2 pr-4 font-normal">Joined</th>
                        <th className="py-2 pr-4 font-normal"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((it) => (
                        <tr
                          key={it.id}
                          className="border-b border-slate-100"
                          data-testid={`admin-beta-row-${it.id}`}
                        >
                          <td className="py-2 pr-4 font-medium text-slate-900">{it.email}</td>
                          <td className="py-2 pr-4 text-slate-500">{it.note || "—"}</td>
                          <td className="py-2 pr-4 text-slate-500">
                            {it.invited_at ? new Date(it.invited_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="py-2 pr-4">
                            {it.used_at ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <Check className="w-4 h-4" /> Joined
                              </span>
                            ) : (
                              <span className="text-slate-400">Pending</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            <button
                              type="button"
                              onClick={() => remove(it.id)}
                              className="inline-flex items-center gap-1 text-slate-400 hover:text-red-600"
                              data-testid={`admin-beta-remove-${it.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}


// ────────────────────────────────────────────────────────────────────
// BetaCodesSection — Mar 2026
// Shared invite codes (e.g. "SOULUMO-BETA") that a tester can type on
// the Soulumo auth page to self-join the allow-list. Simpler than
// adding each tester's email by hand for broadcast launches.
// ────────────────────────────────────────────────────────────────────
function BetaCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [brand, setBrand] = useState("soulumo");
  const [note, setNote] = useState("");
  const [active, setActive] = useState(true);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/beta-codes`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setCodes(d.items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setAdding(true);
    try {
      const r = await fetch(`${API}/api/admin/beta-codes`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ code: trimmed, brand, note, active }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      setCode("");
      setNote("");
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await fetch(`${API}/api/admin/beta-codes`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          code: c.code,
          brand: c.brand,
          note: c.note || "",
          active: !c.active,
        }),
      });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Testers who already joined keep their accounts.`)) return;
    try {
      await fetch(`${API}/api/admin/beta-codes/${c.id}`, { method: "DELETE", headers: auth() });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const copyCode = async (c) => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) { /* clipboard blocked — the code is on screen anyway */ }
  };

  return (
    <section className="mb-10" data-testid="admin-beta-codes-section">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-slate-900 font-semibold flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Shared beta codes
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            A single code anyone can type on the Soulumo sign-up form.
            Use for WhatsApp broadcasts or small group launches.
          </p>
        </div>
      </div>

      <form
        onSubmit={add}
        className="mb-5 bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3"
        data-testid="admin-beta-codes-add-form"
      >
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_1fr_auto] gap-2">
          <input
            type="text"
            required
            placeholder="SOULUMO-BETA"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono tracking-wide"
            data-testid="admin-beta-code-value"
          />
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid="admin-beta-code-brand"
          >
            {BRANDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="note (optional) — launch week"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid="admin-beta-code-note"
          />
          <button
            type="submit"
            disabled={adding || !code.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50"
            data-testid="admin-beta-code-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {adding ? "Saving…" : "Save code"}
          </button>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            data-testid="admin-beta-code-active"
          />
          Active (uncheck to disable without deleting)
        </label>
        {err && (
          <div className="text-sm text-red-600" data-testid="admin-beta-codes-error">{err}</div>
        )}
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : codes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500 text-center">
          No shared codes yet. Create one above (e.g. SOULUMO-BETA).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 font-normal">Code</th>
                <th className="py-2 pr-4 font-normal">Brand</th>
                <th className="py-2 pr-4 font-normal">Note</th>
                <th className="py-2 pr-4 font-normal">Claims</th>
                <th className="py-2 pr-4 font-normal">Status</th>
                <th className="py-2 pr-4 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-slate-100"
                  data-testid={`admin-beta-code-row-${c.id}`}
                >
                  <td className="py-2 pr-4 font-mono font-semibold text-slate-900 tracking-wide">
                    <div className="flex items-center gap-2">
                      <span>{c.code}</span>
                      <button
                        type="button"
                        onClick={() => copyCode(c)}
                        title="Copy code"
                        className="text-slate-400 hover:text-slate-700"
                        data-testid={`admin-beta-code-copy-${c.id}`}
                      >
                        {copiedId === c.id
                          ? <Check className="w-4 h-4 text-emerald-500" />
                          : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-slate-700 capitalize">{c.brand}</td>
                  <td className="py-2 pr-4 text-slate-500">{c.note || "—"}</td>
                  <td className="py-2 pr-4 text-slate-700">{c.claim_count || 0}</td>
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      onClick={() => toggleActive(c)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        c.active
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-slate-300 bg-slate-100 text-slate-500"
                      }`}
                      data-testid={`admin-beta-code-toggle-${c.id}`}
                    >
                      {c.active ? "Active" : "Paused"}
                    </button>
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <button
                      type="button"
                      onClick={() => remove(c)}
                      className="inline-flex items-center gap-1 text-slate-400 hover:text-red-600"
                      data-testid={`admin-beta-code-delete-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
